/**
 * ManagerMiniTrends — Testing Notes 12 · item #5
 * -----------------------------------------------
 * Two compact 30-day trend cards for the manager Home: sales (₹) and expenses
 * (₹). Both cards read from the shared `/api/analytics/dashboard?period=30d`
 * response — no extra endpoint. Deliberately small so they sit beneath the
 * ops queue without dominating the page.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ResponsiveContainer, AreaChart, Area, Tooltip as RechartsTooltip, XAxis, YAxis } from "recharts";
import { TrendingUp, Wallet, ArrowRight } from "lucide-react";

const fmtRs = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
const fmtShortDate = (d) => {
  if (!d) return "";
  try {
    const dt = new Date(`${d}T00:00:00`);
    return dt.toLocaleDateString("en-IN", { weekday: "short", day: "2-digit", month: "short" });
  } catch { return d; }
};

export default function ManagerMiniTrends() {
  const [data, setData] = useState(null);
  useEffect(() => {
    let dead = false;
    api.get("/analytics/dashboard?period=30d")
      .then(({ data }) => !dead && setData(data))
      .catch(() => !dead && setData({ trend: [] }));
    return () => { dead = true; };
  }, []);

  const trend = data?.trend || [];
  const salesTotal = trend.reduce((s, r) => s + Number(r.sales || 0), 0);
  const shortageTotal = trend.reduce((s, r) => s + Number(r.shortage || 0), 0);

  return (
    <div className="grid gap-3 lg:grid-cols-2" data-testid="manager-mini-trends">
      <MiniCard
        testId="manager-sales-trend"
        icon={<TrendingUp className="h-4 w-4 text-primary" />}
        title="Sales · last 30 days"
        subtitle={`Total ${fmtRs(salesTotal)}`}
        trend={trend}
        dataKey="sales"
        gradient="salesGrad"
        linkTo="/manager/hisabs"
        linkLabel="View shifts"
        empty="Sales will chart here after a few shifts."
      />
      <MiniCard
        testId="manager-shortage-trend"
        icon={<Wallet className="h-4 w-4 text-red-600" />}
        title="Shortage · last 30 days"
        subtitle={`Total ${fmtRs(shortageTotal)}`}
        trend={trend}
        dataKey="shortage"
        gradient="shortageGrad"
        color="hsl(0 74% 55%)"
        linkTo="/manager/shortage"
        linkLabel="Shortage ledger"
        empty="No shortage this month — clean books."
      />
    </div>
  );
}

function MiniCard({ testId, icon, title, subtitle, trend, dataKey, gradient, color, linkTo, linkLabel, empty }) {
  const c = color || "hsl(var(--primary))";
  const hasData = trend?.length >= 2;
  return (
    <div className="rounded-2xl border border-border bg-card p-4 shadow-sm" data-testid={testId}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {icon}
          <div>
            <div className="text-sm font-bold text-foreground">{title}</div>
            <div className="text-[10px] text-muted-foreground">{subtitle}</div>
          </div>
        </div>
        <Link to={linkTo} className="flex items-center gap-1 text-[10px] font-semibold text-primary hover:underline">
          {linkLabel} <ArrowRight className="h-3 w-3" />
        </Link>
      </div>
      {!hasData ? (
        <div className="mt-3 flex h-24 items-center justify-center rounded-lg border border-dashed border-border bg-secondary/30 text-xs text-muted-foreground">
          {empty}
        </div>
      ) : (
        <div className="mt-3 h-24 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={trend} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
              <defs>
                <linearGradient id={gradient} x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0%" stopColor={c} stopOpacity={0.3} />
                  <stop offset="100%" stopColor={c} stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="date" hide />
              <YAxis hide />
              <RechartsTooltip content={<MiniTooltip dataKey={dataKey} />} />
              <Area type="monotone" dataKey={dataKey} stroke={c} strokeWidth={2} fill={`url(#${gradient})`} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

function MiniTooltip({ active, payload, dataKey }) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload || {};
  return (
    <div className="rounded-lg border border-border bg-card px-2.5 py-1.5 text-[11px] shadow-lg">
      <div className="font-bold">{fmtShortDate(row.date)}</div>
      <div className="text-muted-foreground">
        {dataKey === "shortage" ? "Shortage " : "Sales "}
        <span className="font-mono font-semibold text-foreground">{fmtRs(row[dataKey])}</span>
      </div>
    </div>
  );
}
